import type { ExamDefinitionDto, ProtocolDefinitionDto } from "@repo/data-store";
import { Badge } from "@repo/ui/components/ui/badge";
import { Button } from "@repo/ui/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@repo/ui/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/components/ui/popover";
import { ChevronsUpDown, XIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { markdownLine } from "@/shared/lib/markdown-line";
import { findProtocolBuilderProcedure, type ProtocolBuilderProcedure } from "./protocol-builder-definition";

type ProcedureType = "Diagnostic" | "Treatment" | "Exam";

interface ProcedureOption {
  canonical: string;
  title: string;
  description?: string;
  procedureType?: ProcedureType;
  selectable?: boolean;
}

interface BasePickerProps {
  value: string | undefined;
  onChange: (canonical: string | undefined) => void;
  /** Render the search + list in place instead of behind a combobox trigger. */
  inline?: boolean;
}

export interface ProgramPickerOption {
  canonical: string;
  title: string;
  description?: string;
}

interface ProtocolPickerProps extends BasePickerProps {
  mode?: "protocol";
  activityDefinitions: ProtocolBuilderProcedure[];
  examDefinitions?: ExamDefinitionDto[];
  planDefinitions?: never;
}

interface ProgramPickerProps extends BasePickerProps {
  mode: "program";
  planDefinitions: ProtocolDefinitionDto[];
  activityDefinitions?: never;
}

type ProcedurePickerProps = ProtocolPickerProps | ProgramPickerProps;

function buildProtocolOptions(activityDefinitions: ProtocolBuilderProcedure[]): ProcedureOption[] {
  return activityDefinitions.map((definition) => ({
    canonical: definition.canonical,
    title: definition.title,
    description: definition.description,
    procedureType: definition.kind,
  }));
}

function buildExamOptions(examDefinitions: ExamDefinitionDto[]): ProcedureOption[] {
  return examDefinitions.map((exam) => ({
    canonical: exam.definitionUrl,
    title: exam.name,
    description: exam.description,
    procedureType: "Exam" as const,
  }));
}

export function buildProgramOptions(planDefinitions: ProtocolDefinitionDto[]): ProcedureOption[] {
  return planDefinitions.map((pd) => ({
    canonical: pd.definitionUrl,
    title: pd.name,
    description: pd.description,
    procedureType: "Diagnostic" as const, // unused in program mode
    selectable: pd.availability === "Active",
  }));
}

export function filterSelectableProcedureOptions(options: ProcedureOption[], search: string): ProcedureOption[] {
  const query = search.toLowerCase();
  return options.filter(
    (option) => option.selectable !== false && (!query || option.title.toLowerCase().includes(query)),
  );
}

export function ProcedurePicker(props: ProcedurePickerProps) {
  const { value, onChange, mode = "protocol", inline } = props;
  const activityDefinitions = props.mode === "program" ? undefined : props.activityDefinitions;
  const examDefinitions = props.mode === "program" ? undefined : props.examDefinitions;
  const planDefinitions = props.mode === "program" ? props.planDefinitions : undefined;

  const allOptions = useMemo(() => {
    if (mode === "program") {
      return buildProgramOptions(planDefinitions ?? []);
    }
    return [...buildExamOptions(examDefinitions ?? []), ...buildProtocolOptions(activityDefinitions ?? [])];
  }, [mode, planDefinitions, activityDefinitions, examDefinitions]);

  return (
    <OptionPicker
      value={value}
      onChange={onChange}
      mode={mode}
      inline={inline}
      options={allOptions}
      activityDefinitions={activityDefinitions}
    />
  );
}

export function ProgramOptionPicker<T extends ProgramPickerOption>({
  value,
  onChange,
  options,
}: {
  value: T | undefined;
  onChange: (option: T | undefined) => void;
  options: T[];
}) {
  return (
    <OptionPicker
      value={value?.canonical}
      onChange={(canonical) =>
        onChange(canonical ? options.find((option) => option.canonical === canonical) : undefined)
      }
      mode="program"
      options={options}
    />
  );
}

function OptionPicker({
  value,
  onChange,
  mode,
  inline,
  options,
  activityDefinitions,
}: BasePickerProps & {
  mode: "protocol" | "program";
  options: ProcedureOption[];
  activityDefinitions?: ProtocolBuilderProcedure[];
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selected = useMemo(() => {
    if (!value) return undefined;
    const exact = options.find((o) => o.canonical === value);
    if (exact) return exact;
    // Fall back to canonical resolution: stored value may be URL form or `ActivityDefinition/<id>` form.
    if (mode !== "program" && activityDefinitions) {
      const ad = findProtocolBuilderProcedure(value, activityDefinitions);
      if (ad) return options.find((o) => o.canonical === ad.canonical);
    }
    return undefined;
  }, [options, value, mode, activityDefinitions]);

  const filtered = useMemo(() => {
    return filterSelectableProcedureOptions(options, search);
  }, [options, search]);

  const groups = useMemo(() => {
    if (mode === "program") {
      return [{ heading: "Protocols", items: filtered }];
    }
    return [
      { heading: "Exams", items: filtered.filter((o) => o.procedureType === "Exam") },
      { heading: "Diagnostics", items: filtered.filter((o) => o.procedureType === "Diagnostic") },
      { heading: "Treatments", items: filtered.filter((o) => o.procedureType === "Treatment") },
    ];
  }, [mode, filtered]);

  const placeholder = mode === "program" ? "Search protocols..." : "Search procedures...";

  const command = (
    <Command shouldFilter={false} className={inline ? "min-h-0 flex-1" : undefined}>
      <CommandInput placeholder={placeholder} value={search} onValueChange={setSearch} />
      <CommandList className={inline ? "max-h-none min-h-0 flex-1" : undefined}>
        {filtered.length === 0 && <CommandEmpty>No catalog definitions found.</CommandEmpty>}
        {groups.map(
          (group) =>
            group.items.length > 0 && (
              <CommandGroup key={group.heading} heading={mode === "program" ? undefined : group.heading}>
                {group.items.map((option) => (
                  <CommandItem
                    key={option.canonical}
                    value={option.canonical}
                    showCheck={!inline}
                    onSelect={() => {
                      onChange(option.canonical);
                      setOpen(false);
                      setSearch("");
                    }}
                    data-checked={option.canonical === value ? "true" : undefined}
                  >
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="text-sm">{option.title}</span>
                      {option.description && (
                        <span className="text-xs text-muted-foreground overflow-hidden whitespace-nowrap">
                          {markdownLine(option.description)}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ),
        )}
      </CommandList>
    </Command>
  );

  if (inline) return command;

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <div className="relative">
          <PopoverTrigger
            render={<Button variant="outline" className="w-full justify-between text-sm font-normal h-9" />}
          >
            {selected ? (
              <span className="flex items-center gap-2 truncate">
                {mode !== "program" && (
                  <Badge variant="default" className="shrink-0">
                    {selected.procedureType}
                  </Badge>
                )}
                <span className="truncate">{selected.title}</span>
              </span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            {!selected && <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />}
          </PopoverTrigger>
          {selected && (
            <Button
              variant="ghost"
              size="icon-xs"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100"
              onClick={() => onChange(undefined)}
            >
              <XIcon className="size-4" />
            </Button>
          )}
        </div>
        <PopoverContent className="w-[var(--anchor-width)] p-0" align="start">
          {command}
        </PopoverContent>
      </Popover>
    </div>
  );
}
